import React from 'react';

export interface StatusBadgeProps {
  status: string;
  variant?: 'default' | 'success' | 'warning' | 'error' | 'info' | 'purple' | 'neutral';
  size?: 'sm' | 'md' | 'lg';
  icon?: React.ReactNode;
  className?: string;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  variant = 'default',
  size = 'md',
  icon,
  className = '',
}) => {
  // Auto-detect variant based on status if not explicitly set
  const getVariantFromStatus = (status: string): string => {
    const statusLower = status.toLowerCase();

    // Task/Project status mappings
    if (
      statusLower.includes('completed') ||
      statusLower.includes('done') ||
      statusLower.includes('approved')
    ) {
      return 'success';
    }
    if (
      statusLower.includes('progress') ||
      statusLower.includes('active') ||
      statusLower.includes('pending')
    ) {
      return 'info';
    }
    if (
      statusLower.includes('hold') ||
      statusLower.includes('paused') ||
      statusLower.includes('warning')
    ) {
      return 'warning';
    }
    if (
      statusLower.includes('cancelled') ||
      statusLower.includes('failed') ||
      statusLower.includes('error') ||
      statusLower.includes('rejected')
    ) {
      return 'error';
    }
    if (statusLower.includes('review') || statusLower.includes('testing')) {
      return 'purple';
    }

    // Priority mappings
    if (statusLower.includes('critical')) {
      return 'error';
    }
    if (statusLower.includes('high')) {
      return 'warning';
    }
    if (statusLower.includes('medium')) {
      return 'info';
    }
    if (statusLower.includes('low')) {
      return 'success';
    }

    return 'neutral';
  };

  const finalVariant = variant === 'default' ? getVariantFromStatus(status) : variant;

  const getVariantClasses = (variant: string): string => {
    switch (variant) {
      case 'success':
        return 'badge-success';
      case 'warning':
        return 'badge-warning';
      case 'error':
        return 'badge-error';
      case 'info':
        return 'badge-info';
      case 'purple':
        return 'badge-purple';
      case 'neutral':
      default:
        return 'badge-neutral';
    }
  };

  const getSizeClasses = (size: string): string => {
    switch (size) {
      case 'sm':
        return 'text-xs px-2 py-0.5';
      case 'lg':
        return 'text-sm px-3 py-1';
      case 'md':
      default:
        return 'text-xs px-2.5 py-0.5';
    }
  };

  const formatStatus = (status: string): string => {
    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  return (
    <span
      className={`
        badge
        ${getVariantClasses(finalVariant)}
        ${getSizeClasses(size)}
        ${className}
      `.trim()}
    >
      {icon && <span className="mr-1">{icon}</span>}
      {formatStatus(status)}
    </span>
  );
};

export default StatusBadge;
